/**
 * Spread Checkpoint Hooks
 * React hooks for fetching and managing spread checkpoint data
 */
import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { queryKeys } from '../query-keys'
import { SpreadCheckpoint } from '@/api/types/spread-checkpoint'
import { spreadCheckpointService } from '../services/spread-checkpoint.service'

function parseExpirationMs(value: unknown): number | null {
  if (typeof value !== 'string' || !value) return null
  const ms = Date.parse(value)

  return Number.isFinite(ms) ? ms : null
}

function getEarliestSignedExpirationMs(data: SpreadCheckpoint | undefined): number | null {
  if (!data) return null

  const expirations: number[] = []

  // Backend may return either SignedExpiration or signedExpiration. Support both.
  const rootExpirations = [
    parseExpirationMs(data.metadata.signedExpiration),
    parseExpirationMs(data.metadata.SignedExpiration),
  ]

  for (const ms of rootExpirations) {
    if (typeof ms === 'number') expirations.push(ms)
  }

  const refs = data.elementReferences ?? data.elements.map((e) => e.reference)

  for (const ref of refs) {
    const ms = parseExpirationMs(ref.signedExpiration)

    if (typeof ms === 'number') expirations.push(ms)
  }

  if (expirations.length === 0) return null
  return Math.min(...expirations)
}

/**
 * Options for useSpreadCheckpoint hook
 */
export interface UseSpreadCheckpointOptions extends Omit<
  UseQueryOptions<SpreadCheckpoint, Error>,
  'queryKey' | 'queryFn'
> {
  /**
   * Whether to automatically decode all elements
   * @default true
   */
  decodeElements?: boolean

  /**
   * If set, decodes only the first N extracted element references.
   * Useful for progressive/on-demand element decoding to reduce initial load.
   */
  decodeElementsLimit?: number
}

/**
 * Hook for fetching a spread checkpoint
 *
 * @param spreadId - The ID of the spread checkpoint to fetch
 * @param options - Additional query options
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useSpreadCheckpoint('cfad45bf-7ecd-4edb-b8aa-d56cd0baa809')
 *
 * if (isLoading) return <div>Loading...</div>
 * if (error) return <div>Error: {error.message}</div>
 *
 * return <div>Checkpoint has {data.elements.length} elements</div>
 * ```
 */
export function useSpreadCheckpoint(spreadId: string, options?: UseSpreadCheckpointOptions) {
  const { decodeElements = true, decodeElementsLimit, ...queryOptions } = options || {}

  return useQuery({
    queryKey: queryKeys.spreadCheckpoint.byId(spreadId, { decodeElements, decodeElementsLimit }),
    queryFn: () =>
      spreadCheckpointService.getSpreadCheckpoint(spreadId, {
        decodeElements,
        decodeElementsLimit,
      }),
    retry: 2,
    refetchOnWindowFocus: false,
    // Signed URLs are long-lived (hours). Keep query data fresh longer to reduce API load,
    // but proactively refetch shortly before signed URLs expire to avoid S3 403s.
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache for a while
    refetchInterval: (query) => {
      const data = query.state.data as SpreadCheckpoint | undefined
      const earliestExpMs = getEarliestSignedExpirationMs(data)

      if (!earliestExpMs) return false

      const now = Date.now()
      const safetyWindowMs = 10 * 60 * 1000 // 10 minutes
      const msUntilRefetch = earliestExpMs - now - safetyWindowMs

      // If already expired / within the safety window, refetch soon.
      if (msUntilRefetch <= 0) return 5 * 1000

      // Otherwise schedule a single refetch close to expiry.
      return msUntilRefetch
    },
    refetchIntervalInBackground: false,
    ...queryOptions,
  })
}
